import { api } from './client'

/**
 * Tenant server settings — wraps cb-api's /v1/settings/server endpoint.
 *
 * Centerbase tenants store schema-dependent config (custom field IDs that
 * change per tenant — like which Matter field holds the responsible
 * attorney) in CB's `Get_GetServerSetting` table. The .NET reports look
 * these up at runtime; we do the same instead of hard-coding IDs from a
 * single demo tenant.
 *
 * All settings are returned as strings (CB's storage type). Callers parse
 * to int via toInt() below — missing/invalid values come back as 0,
 * which mirrors `requestManager.GetServerSettingInt(key, 0)` on the .NET
 * side.
 */

const settingsService = {
  /**
   * Fetch one or more server settings by key.
   * @param {string[]} keys — settings keys to fetch (e.g. ['BillableClientLookup'])
   * @returns Promise<Record<string,string>>  — { key: value }
   */
  async getServerSettings(keys) {
    if (!keys || !keys.length) return {}
    const data = await api.get('/settings/server', { keys: keys.join(',') })
    return data?.data || {}
  },

  /** Parse a setting value to int with a default. Mirrors GetServerSettingInt. */
  toInt(value, defaultValue = 0) {
    const n = parseInt(value, 10)
    return Number.isFinite(n) ? n : defaultValue
  },

  /**
   * Parse a setting value to bool with a default. Mirrors
   * `GetServerSettingBool(key, defaultVal)` → `MesaUtilsBase.ParseBoolean`:
   * an absent/empty value (cb-api returns '' for unset keys) or anything
   * unrecognized yields the CALLER's default — it never silently means false.
   */
  toBool(value, defaultValue = false) {
    if (value === true || value === false) return value
    const s = String(value ?? '').trim().toLowerCase()
    if (s === '') return defaultValue
    if (s === 'true' || s === '1' || s === 'yes') return true
    if (s === 'false' || s === '0' || s === 'no') return false
    return defaultValue
  },

  /**
   * Firm-wide Report Center switches + whether this caller may change them.
   * → { dashboardsEnabled, canManage, permissionResolved }
   *
   * Only the Settings (admin) surface should call this: it makes cb-api ask CB
   * for the caller's Customize permission. The read-only GATE path uses
   * getServerSettings([DASHBOARDS_SETTING]) instead, which is micro-cached
   * per (tenant,key) in cb-api and costs no permission round-trip.
   */
  async getReportCenter() {
    const data = await api.get('/settings/report-center')
    return data?.data || {}
  },

  /** Turn the Dashboards tab on/off for the whole firm. Admin only (cb-api + CB both gate it). */
  async setFirmDashboards(enabled) {
    const data = await api.put('/settings/report-center/dashboards', { enabled: !!enabled })
    return data?.data || {}
  },
}

export default settingsService
